// Catalog.ID Client — Delegate Detail Pane
// ============================================================================
// Shows delegate identity details with management actions.

import { createEntityPaneType } from '@catalog-shell';
import type { EntityPaneConfig, ViewFieldDef, EntityAction } from '@catalog-shell';
import * as delegateApi from '../api/delegate';
import * as organizationApi from '../api/organization';
import * as accountApi from '../api/account';
import { formatApiError } from '../api/client';
import { unwrapPrivateKey, wrapPrivateKey } from '../crypto/claims';
import * as aes from '../crypto/aes';
import { keys } from '../state/keys';
import { getEntitledOrgKeys } from '../state/org-key-entitlements';
import type { DelegateSummary } from '../api/types';
import { identity } from '../state/identity';
import { PANE_IDS } from '../shell-config';
import { signStepUp } from '../auth/step-up';
import { liveAgentsMessage, withAgentAcknowledgement } from '../auth/principal-termination';

const viewFields: ViewFieldDef<DelegateSummary>[] = [
    { key: 'cid', label: 'CID', type: 'text' },
    {
        key: 'roles', label: 'Roles', type: 'text',
        format: (v) => Array.isArray(v) ? v.join(', ') : String(v),
    },
    {
        key: 'status', label: 'Status', type: 'badge',
        colorMap: {
            active: 'rgba(80,200,80,0.3)',
            partial: 'rgba(100,149,237,0.3)',
            frozen: 'rgba(220,150,50,0.3)',
            terminated: 'rgba(220,50,50,0.3)',
        },
        labelMap: {
            active: 'Active',
            partial: 'Pending Onboarding',
            frozen: 'Frozen',
            terminated: 'Terminated',
        },
    },
    { key: 'creationTime', label: 'Created', type: 'date', format: 'datetime' },
];

const actions: EntityAction<DelegateSummary>[] = [
    {
        key: 'complete-onboarding',
        label: 'Complete Onboarding',
        modes: ['view'],
        canExecute: (model) => model.status === 'partial',
        disabledReason: () => 'Only a delegate pending onboarding can be completed',
        execute: async (model, ctx) => {
            try {
                // 1. Fetch our own wrapped org keys
                const myWrappedKeys = await delegateApi.getWrappedOrgKeys();
                if (myWrappedKeys.length === 0) {
                    ctx.shell.toast('No org keys available — cannot complete onboarding', 'error');
                    return;
                }

                // 2. Unwrap them with our own attr_encrypt private key
                const mySecretKey = keys.getPrivateKey('attr_encrypt');
                if (!mySecretKey) {
                    ctx.shell.toast('Private key not loaded — unlock keys first', 'error');
                    return;
                }

                // 3. Fetch the new delegate's public key
                const targetKeys = await accountApi.getPublicKeys(model.cid);
                const targetAttr = targetKeys.find(k => k.keyId === 'attr_encrypt');
                if (!targetAttr) {
                    ctx.shell.toast('Target delegate has no attr_encrypt key', 'error');
                    return;
                }

                // 4. Determine which org keys the target delegate's roles permit (§3a.4).
                //    Shared mirror of the server's KnownKeyTypes.GetEntitledOrgKeys —
                //    the local copy that used to live here had drifted (no efs_encrypt).
                const roles = model.roles ?? [];
                const permittedKeys = getEntitledOrgKeys(roles);

                // 5. Unwrap permitted org keys, re-wrap to new delegate's public key
                const newWrappedKeys = [];
                for (const wk of myWrappedKeys) {
                    if (!permittedKeys.has(wk.keyId)) continue;
                    const orgPrivateKeyBase64 = await unwrapPrivateKey(wk.wrappedKey, aes.fromBase64(mySecretKey));
                    const reWrapped = await wrapPrivateKey(orgPrivateKeyBase64, aes.fromBase64(targetAttr.publicKey));
                    newWrappedKeys.push({
                        keyId: wk.keyId,
                        keyIndex: wk.keyIndex,
                        wrappedKey: reWrapped,
                    });
                }

                // 5. Submit
                await delegateApi.completeOnboarding(model.cid, { wrappedOrgKeys: newWrappedKeys });
                ctx.shell.toast('Delegate onboarding completed', 'success');
                ctx.entityView.reload();
            } catch (err) {
                ctx.shell.toast(formatApiError(err, 'Onboarding failed'), 'error');
            }
        },
    },
    {
        key: 'change-roles',
        label: 'Change Roles',
        modes: ['view'],
        canExecute: (model) => model.status === 'active' || model.status === 'partial',
        disabledReason: () => 'Roles can only be changed for an active or pending delegate',
        execute: async (model, ctx) => {
            const orgCid = identity.getOrganizationCid();
            if (!orgCid) return;

            const allRoles = ['owner', 'admin', 'treasurer', 'viewer'];
            const currentRoles = new Set(model.roles);

            const content = document.createElement('div');
            content.className = 'cid-wizard-step';

            for (const role of allRoles) {
                const row = document.createElement('div');
                row.className = 'cid-auth-checkbox-row';
                const cb = document.createElement('input');
                cb.type = 'checkbox';
                cb.id = `role-${role}`;
                cb.checked = currentRoles.has(role);
                cb.addEventListener('change', () => {
                    if (cb.checked) currentRoles.add(role);
                    else currentRoles.delete(role);
                });
                const label = document.createElement('label');
                label.textContent = role.charAt(0).toUpperCase() + role.slice(1);
                label.setAttribute('for', cb.id);
                row.appendChild(cb);
                row.appendChild(label);
                content.appendChild(row);
            }

            const submitBtn = document.createElement('button');
            submitBtn.className = 'cs-btn cs-btn--yes';
            submitBtn.textContent = 'Update Roles';
            content.appendChild(submitBtn);

            const modal = ctx.shell.showModal(content);

            submitBtn.addEventListener('click', async () => {
                if (currentRoles.size === 0) {
                    ctx.shell.toast('Select at least one role', 'warning');
                    return;
                }
                submitBtn.setAttribute('disabled', '');
                try {
                    await delegateApi.updateRoles(orgCid, model.cid, {
                        roles: Array.from(currentRoles),
                    });
                    modal.close();
                    ctx.shell.toast('Roles updated', 'success');
                    ctx.entityView.reload();
                } catch (err) {
                    submitBtn.removeAttribute('disabled');
                    ctx.shell.toast(formatApiError(err, 'Failed to update roles'), 'error');
                }
            });
        },
    },
    {
        key: 'freeze',
        label: 'Freeze',
        modes: ['view'],
        overflow: true,
        variant: 'danger',
        // Nobody freezes themselves (docs/cid-governance.md M15).
        canExecute: (model) => model.status === 'active' && model.cid !== identity.getActive()?.cid,
        disabledReason: (model) => model.cid === identity.getActive()?.cid
            ? 'You cannot freeze yourself'
            : 'Only an active delegate can be frozen',
        execute: async (model, ctx) => {
            const ok = await ctx.shell.confirm({
                title: 'Freeze Delegate',
                message: `Freeze ${model.cid}? This will halt all API access for this delegate.`,
                confirmLabel: 'Freeze',
                confirmVariant: 'no',
            });
            if (!ok) return;
            try {
                await delegateApi.freezeDelegate(model.cid);
                ctx.shell.toast('Delegate frozen', 'success');
                ctx.entityView.reload();
            } catch (err) {
                ctx.shell.toast(formatApiError(err, 'Failed to freeze'), 'error');
            }
        },
    },
    {
        key: 'unfreeze',
        label: 'Unfreeze',
        modes: ['view'],
        overflow: true,
        canExecute: (model) => model.status === 'frozen',
        disabledReason: () => 'Only a frozen delegate can be unfrozen',
        execute: async (model, ctx) => {
            try {
                await delegateApi.unfreezeDelegate(model.cid);
                ctx.shell.toast('Delegate unfrozen', 'success');
                ctx.entityView.reload();
            } catch (err) {
                ctx.shell.toast(formatApiError(err, 'Failed to unfreeze'), 'error');
            }
        },
    },
    {
        key: 'terminate',
        label: 'Terminate',
        modes: ['view'],
        overflow: true,
        variant: 'danger',
        canExecute: (model) => model.status !== 'terminated',
        disabledReason: () => 'This delegate is already terminated',
        execute: async (model, ctx) => {
            const orgCid = identity.getOrganizationCid();
            if (!orgCid) return;
            const ok = await ctx.shell.confirm({
                title: 'Terminate Delegate',
                message: `Permanently terminate ${model.cid}? This cannot be undone. The username will be retired.`,
                confirmLabel: 'Terminate',
                confirmVariant: 'no',
            });
            if (!ok) return;
            try {
                // Irreversible, so the node asks for a signature by YOUR key, not
                // just the session (docs/cid-governance.md M15); and a delegate that
                // is the principal of live agents is removed only once you agree
                // they end with it.
                const outcome = { rotationRequired: false };
                const done = await withAgentAcknowledgement(async (terminateAgents) => {
                    const challengeSignature = await signStepUp('delegate-remove');
                    const result = await delegateApi.terminateDelegate(orgCid, model.cid, { challengeSignature, terminateAgents });
                    outcome.rotationRequired = result?.rotationRequired === true;
                }, (agents) => ctx.shell.confirm({
                    title: 'Terminate the Delegate\'s Agents',
                    message: liveAgentsMessage(agents, `${model.cid} is`),
                    confirmLabel: 'Terminate Delegate and Agents',
                    confirmVariant: 'no',
                    requireExplicitChoice: true,
                }));
                if (!done) return;
                ctx.shell.toast('Delegate terminated', 'success');
                ctx.entityView.reload();
                if (outcome.rotationRequired) {
                    ctx.shell.toast('Organization key rotation required. Go to Organization settings to rotate keys.', 'warning');
                }
            } catch (err) {
                ctx.shell.toast(formatApiError(err, 'Termination failed'), 'error');
            }
        },
    },
];

const config: EntityPaneConfig<DelegateSummary> = {
    typeId: PANE_IDS.delegateDetail,
    defaultTitle: 'Delegate',
    ariaLabel: 'Delegate details',
    refreshOnDataChanged: 'delegates',
    // Verbs live in the pane's own title row (shell USAGE.md "Where a button
    // belongs"): the onboarding/roles verbs inline, the lifecycle switches
    // (Freeze / Unfreeze / Terminate) under the trailing ⋯ menu.
    actionsPlacement: 'pane',

    getModel: async (ctx) => {
        // Re-fetch from server to get fresh status (e.g., after freeze/unfreeze)
        const delegateCid = (ctx.instance.context['delegate'] as DelegateSummary)?.cid
            ?? ctx.instance.context['delegateCid'] as string;
        const orgCid = identity.getOrganizationCid();
        if (orgCid && delegateCid) {
            try {
                const delegates = await organizationApi.listDelegates(orgCid);
                const fresh = delegates.find(d => d.cid === delegateCid);
                if (fresh) {
                    ctx.instance.context['delegate'] = fresh;
                    return fresh;
                }
            } catch { /* fall through to stale context */ }
        }
        return ctx.instance.context['delegate'] as DelegateSummary;
    },

    view: {
        fields: viewFields,
        layout: [
            ['cid', 'status'],
            ['roles', 'creationTime'],
        ],
    },

    canEdit: false,
    canDelete: false,
    actions,
};

export function createDelegateDetailPane() {
    return createEntityPaneType(config);
}
